/** Normalized client contract for existing Nest, validation and provider errors.
 * Keep the original response separately; unvalidated payload properties must not
 * become control-flow inputs or be assumed to have this shape.
 */
export interface ApiErrorContract {
  message: string
  status?: number
  code?: string
}

const record = (value: unknown): Record<string, unknown> | undefined =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined

export function readApiErrorMessage(
  value: unknown,
  depth = 0,
): string | undefined {
  if (depth > 8) return undefined
  if (typeof value === 'string') return value.trim() ? value : undefined
  if (Array.isArray(value)) return readApiErrorMessage(value[0], depth + 1)
  const data = record(value)
  if (!data) return undefined
  if (typeof data.message === 'string' && data.message.trim())
    return data.message
  if (Array.isArray(data.message)) {
    const messages = data.message.filter(
      (message): message is string =>
        typeof message === 'string' && Boolean(message.trim()),
    )
    if (messages.length) return messages.join(', ')
  }
  const nested = readApiErrorMessage(data.errors, depth + 1)
  if (nested) return nested
  if (typeof data.error === 'string' && data.error.trim()) return data.error
  if (typeof data.detail === 'string' && data.detail.trim()) return data.detail
  return readApiErrorMessage(data.error, depth + 1)
}

export function readApiErrorStatus(value: unknown): number | undefined {
  const data = record(value)
  const status = data?.status ?? data?.statusCode
  return typeof status === 'number' &&
    Number.isInteger(status) &&
    status >= 400 &&
    status <= 599
    ? status
    : undefined
}

export function normalizeApiError(
  value: unknown,
  fallback = 'An error occurred',
  responseStatus?: number,
): ApiErrorContract {
  const data = record(value)
  const code = data?.code
  return {
    message: readApiErrorMessage(value) ?? fallback,
    // An actual HTTP status takes precedence over untrusted response content.
    status:
      readApiErrorStatus({ status: responseStatus }) ??
      readApiErrorStatus(value),
    code:
      typeof code === 'string' && /^[A-Z][A-Z0-9_]{0,127}$/.test(code)
        ? code
        : undefined,
  }
}

export function parseApiErrorText(
  errorText: string,
  fallback = 'An error occurred',
): { message: string; data?: unknown } {
  const payload = errorText
    .trim()
    .replace(/^API Error\s+\d+:\s*/i, '')
    .trim()
  if (!payload) return { message: fallback }
  if (payload.startsWith('{') || payload.startsWith('[')) {
    try {
      const data: unknown = JSON.parse(payload)
      return { message: readApiErrorMessage(data) ?? fallback, data }
    } catch {
      // Keep the legacy text response contract when a proxy returns non-JSON.
    }
  }
  return { message: payload, data: payload }
}

/** A transport failure can leave a write committed with its response lost. */
export function isLostResponseError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    (error instanceof Error && error.name === 'TimeoutError') ||
    (typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'TimeoutError')
  )
}
